import { environment } from '../../environments/environment';
import { Subscription, throwError } from 'rxjs';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone, HostListener, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from '../app.service';
import { ProjectItem } from '../project/index';
import { MessageBrokerService } from '../shared/core/index';
import { AuthService } from '../shared/core/auth.service';
import { IUserInfo } from '../shared/core/user-info.model';
import { GlobalErrorHandler } from '../shared/core/error-handler';
import { HttpService } from '../shared/core/http.service';
import { Observable } from 'rxjs';
import { ProjectService } from '../project/project.service';
import { IProject } from '../project/project.model';
import { map } from 'rxjs/internal/operators/map';
import { tap } from 'rxjs/internal/operators/tap';
import { catchError } from 'rxjs/internal/operators/catchError';
import { DashboardService, DashboardDto, DashboardCardDto } from './dashboard.service';

@Component({
  selector: 'dashboard',
  standalone: false,
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, OnDestroy {

  showNoProjectsMessage: boolean;
  noProjectMessage: string = 'Can\'t display the last accessed project or could not find the requested project';
  additionalNoProjectMessage: string;
  currentProjectId: string;
  project: ProjectItem = new ProjectItem();
  statuses: [string, number][] = [];
  isDataLoading: boolean;
  currentUser: IUserInfo;
  projectDepartment: string = '';
  errorMessage: string;

  showSettingsModal: boolean = false;

  dashboardCards: DashboardCardDto[] = [];
  allDashboards: DashboardDto[] = [];
  selectedDashboardId: number = null;
  showDashboardSelector: boolean = false;
  isDefaultDashboard: boolean = false;
  dashboardDropdownOpen: boolean = false;
  dashboardsLoading: boolean = false;

  private APIUrl = environment.apiUrl;

  constructor(
    public route: ActivatedRoute,
    private _appService: AppService,
    private _messageBrokerService: MessageBrokerService,
    private _projectService: ProjectService,
    private _dashboardService: DashboardService,
    private _elementRef: ElementRef,
    private router: Router,
    public _authService: AuthService,
    private _errorHandler: GlobalErrorHandler,
    private _httpService: HttpService,
    private cd: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
    this._authService.subscribeToLoginService().subscribe(r => { this.currentUser = r; });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.dashboardDropdownOpen) {
      const wrapper = this._elementRef.nativeElement.querySelector('.dashboard-select-wrapper');
      if (wrapper && !wrapper.contains(event.target)) {
        this.dashboardDropdownOpen = false;
      }
    }
  }

  sub: Subscription;
  private isValid = false;

  public ngOnInit() {
    if (typeof this._authService.userInfo.accessToken !== 'undefined') {
      console.log('DashboardComponent.ngOnInit()');

      this.isDataLoading = true;
      localStorage.removeItem('AdvancedSearchData');
      this.sub = this.route.params.subscribe(
        params => {
          this._appService.isProjectLoaded = false;
          this.currentProjectId = params['projectId'];
          this.loadDashboard();
        },
        err => {
          if (err.status && err.status === 401) {
            this.loadDashboard();
          } else {
            this._errorHandler.processError(err);
          }
        });
    }
  }

  getUserInfo(email: string): Observable<boolean> {
    return this._httpService.httpGet(this.APIUrl + 'user/GetUsers').pipe(
      map(res => res.json()),
      tap(res => {
        if (res != null) {
          let userData = res;
          return true;
        } else {
          return false;
        }
      }),
      catchError(this.handleError));
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  doProjectDetail(): void {
    this.router.navigate(['projectdetail', this.project.ProjectId]);
  }

  loadDashboard(): void {
    this.isDataLoading = true;
    this.showNoProjectsMessage = false;

    if (!this.currentProjectId) {
      this.currentProjectId = '';
    }

    this._appService.getDashboardProject(this.currentProjectId).subscribe(
      proj => {
        this.ngZone.run(() => {
          this.isDataLoading = false;
          if (proj != null && proj.length > 0) {
            this.project = proj[0];
            if (this.project.StatusCount) {
              this.statuses = Object.entries(this.project.StatusCount).map(
                ([k, v]) => [k, Number(v)] as [string, number]
              );
            }
            this.validateTVProject(this.project.ProjectId);
            this._messageBrokerService.setCurrentProject(this.project, true);
            this.loadResolvedDashboard();
          }
          this.cd.detectChanges();
        });
      },
      err => {
        this.ngZone.run(() => {
          console.error('DashboardComponent.loadDashboard():' + err);
          this.isDataLoading = false;
          if (err.status == 404) {
            this.showNoProjectsMessage = true;
            if (this.currentProjectId) {
              this.noProjectMessage = 'The project you tried to access does not exist or has been deleted';
              this.additionalNoProjectMessage = '';
            } else {
              if (this.isClearanceUser) {
                this.noProjectMessage = 'It seems there are no projects or you have not accessed any yet';
                this.additionalNoProjectMessage = 'Try searching for one first';
              } else {
                this.noProjectMessage = 'You are not currently assigned to any project in this application';
                this.additionalNoProjectMessage = 'Please contact the Clearance team so that you may be added to a project';
              }
            }
            this._messageBrokerService.setCurrentProject(null);
          } else if (err.status == 403) {
            this.showNoProjectsMessage = true;
            this.noProjectMessage = 'You do not have access to this project.';
            this.additionalNoProjectMessage = '';
          } else {
            this._errorHandler.processError(err);
          }
          this.cd.detectChanges();
        });
      }
    );
  }

  goToItemList(sortByField?: string, sortOrder?: string, status?: string, filterBy?: string, filterString?: string, hasNotes: boolean = false): void {
    if (!sortByField) {
      sortByField = 'ModifiedDate';
    }

    var qp: { [type: string]: string } = {};
    qp['sortBy'] = sortByField ? sortByField : 'CreatedDate';
    qp['sortOrder'] = sortOrder ? sortOrder : 'DESC';

    if (status) {
      qp['status'] = status;
    }
    if (filterBy && filterString) {
      qp['filterBy'] = filterBy;
      qp['filterString'] = filterString;
    }

    if (hasNotes) {
      qp['hasNotes'] = 'true';
    }

    this.router.navigate(['projects', this.project.ProjectId, 'items'], { queryParams: qp });
  }

  createNewItem(): void {
    this._appService.listViewURL = '/dashboard/' + this.project.ProjectId;
    this.router.navigate(['projects', this.project.ProjectId, 'items', 'new']);
  }

  get isProductionUser(): Boolean {
    return this._authService.isUserInRole(environment.productionRole) || !environment.production;
  }

  get isClearanceUser(): Boolean {
    return this._authService.isUserInRole(environment.clearanceRole);
  }

  validateTVProject(projectId: string): void {
    if (this.currentProjectId != '') {
      projectId = this.currentProjectId;
    }
    if (projectId != '') {
      this._projectService.getProject(projectId)
        .subscribe((project: IProject) => {
          this.ngZone.run(() => {
            this.projectDepartment = project.department;
            this.cd.detectChanges();
          });
        },
          (error: any) => {
            this.ngZone.run(() => {
              this.errorMessage = <any>error;
              this._errorHandler.processError(error);
              this.cd.detectChanges();
            });
          });
    }
  }

  openEmail(emailId: string, e: Event) {
    if (emailId) {
      window.location.href = 'mailto:' + emailId;
    }
    e.stopPropagation();
  }

  loadResolvedDashboard(selectId?: number): void {
    this.dashboardsLoading = true;
    this.dashboardCards = [];
    this.allDashboards = [];
    this.showDashboardSelector = false;
    this._dashboardService.getAllProjectDashboards(this.project.ProjectId)
      .subscribe(
        (dashboards) => {
          this.allDashboards = dashboards || [];
          // If a specific dashboard was just created/updated, select it so it
          // shows immediately; otherwise fall back to the default resolution.
          const target = selectId != null
            ? this.allDashboards.find(d => d.Id === selectId)
            : null;
          if (target) {
            this.showDashboardSelector = this.allDashboards.length > 1;
            this.selectDashboard(target.Id);
          } else {
            this.autoSelectDashboard();
          }
          this.dashboardsLoading = false;
          this.cd.detectChanges();
        },
        (error) => {
          console.log('DashboardComponent.loadResolvedDashboard() >> error: ' + error);
          this.dashboardsLoading = false;
          this.cd.detectChanges();
        }
      );
  }

  private autoSelectDashboard(): void {
    if (this.allDashboards.length === 0) {
      return;
    }

    this.showDashboardSelector = this.allDashboards.length > 1;

    let selected = this.allDashboards.find(d => d.Default && d.Type === 'Project');
    if (!selected) {
      selected = this.allDashboards.find(d => d.Default && d.Type === 'Department');
    }
    if (!selected) {
      selected = this.allDashboards[0];
    }

    this.selectDashboard(selected.Id);
  }

  selectDashboard(dashboardId: number): void {
    this.selectedDashboardId = dashboardId;
    const db = this.allDashboards.find(d => d.Id === dashboardId);
    if (db && db.Cards) {
      this.dashboardCards = db.Cards.sort((a, b) => a.DisplayOrder - b.DisplayOrder);
    } else {
      this.dashboardCards = [];
    }
    this.updateIsDefault();
  }

  onDashboardChange(dashboardId: string): void {
    this.selectDashboard(Number(dashboardId));
  }

  toggleDashboardDropdown(): void {
    this.dashboardDropdownOpen = !this.dashboardDropdownOpen;
  }

  selectDashboardFromDropdown(id: number): void {
    this.selectDashboard(id);
    this.dashboardDropdownOpen = false;
  }

  get selectedDashboardName(): string {
    const db = this.allDashboards.find(d => d.Id === this.selectedDashboardId);
    return db ? db.Name : '';
  }

  setAsDefault(dashboardId: number): void {
    this._dashboardService.setDefaultDashboard(dashboardId, this.project.ProjectId)
      .subscribe(
        () => {
          this.allDashboards.forEach(d => d.Default = false);
          const db = this.allDashboards.find(d => d.Id === dashboardId);
          if (db) {
            db.Default = true;
          }
          this.updateIsDefault();
          this.cd.detectChanges();
        },
        (error) => {
          console.log('DashboardComponent.setAsDefault() >> error: ' + error);
        }
      );
  }

  private updateIsDefault(): void {
    const projectDefault = this.allDashboards.find(d => d.Default && d.Type === 'Project');
    if (projectDefault) {
      this.isDefaultDashboard = this.selectedDashboardId === projectDefault.Id;
      return;
    }
    const deptDefault = this.allDashboards.find(d => d.Default && d.Type === 'Department');
    if (deptDefault) {
      this.isDefaultDashboard = this.selectedDashboardId === deptDefault.Id;
      return;
    }
    this.isDefaultDashboard = false;
  }

  onCardClick(card: DashboardCardDto): void {
    switch (card.Type) {
      case 'Status':
        this.goToItemList('ModifiedDate', 'DESC', card.Label, '', '');
        break;
      case 'Tag':
        this.goToItemList('', '', '', 'Tags', card.Label);
        break;
      case 'Restriction':
        this.goToItemList('', '', '', 'Restrictions', card.Label);
        break;
      case 'Chat':
        this.goToItemList('ModifiedDate', 'DESC', '', '', '', true);
        break;
      case 'All':
        this.goToItemList('', '', '', '', '');
        break;
      case 'AllPending':
        this.goToItemList('ModifiedDate', 'DESC', 'All Pending', '', '');
        break;
      case 'Urgent':
        this.goToItemList('Deadline', 'DESC', 'Urgent', '', '');
        break;
      case 'StatusChange':
        this.goToItemList('ModifiedDate', 'DESC', '', '', '');
        break;
      default:
        this.goToItemList('', '', '', '', '');
        break;
    }
  }

  private cardDisplayLabels: { [key: string]: string } = {
    'Art Department': 'ART',
    'Ad/Pub': 'Ad/Pub'
  };

  getCardDisplayLabel(card: DashboardCardDto): string {
    if (this.cardDisplayLabels[card.Label]) {
      return this.cardDisplayLabels[card.Label];
    }
    if (card.Type === 'Status' || card.Type === 'SecondStatus'
        || card.Type === 'AllPending') {
      return card.Label;
    }
    return card.Label.toUpperCase();
  }

  private handleError(error: Response) {
    return throwError(() => error || 'Server error');
  }
}
